import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, Trash2, RefreshCw, Users, CalendarDays, Phone, Mail, CalendarOff, BellRing, Instagram } from "lucide-react";
import Logo from "../components/Logo";
import { api, useAuth } from "../context/AuthContext";

const STATI = ["tutte", "in attesa", "confermata", "annullata"];

const badgeCls = {
  "in attesa": "bg-[#C9A265]/15 text-[#C9A265] border-[#C9A265]/40",
  confermata: "bg-emerald-900/40 text-emerald-300 border-emerald-700/50",
  annullata: "bg-red-950/50 text-red-300 border-red-800/50",
};

function formatData(d) {
  if (!d) return "";
  const [y, m, g] = d.split("-");
  return `${g}/${m}/${y}`;
}

export default function Admin() {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filter, setFilter] = useState("tutte");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [newClosedDate, setNewClosedDate] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations");
      setReservations(data);
    } catch {
      toast.error("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setSettings(r.data || {});
        setNotifyEmail(r.data?.notification_email || "");
        setInstagramUrl(r.data?.instagram_url || "");
        setFacebookUrl(r.data?.facebook_url || "");
      })
      .catch(() => setSettings({}));
  }, []);

  const setStato = async (id, stato) => {
    try {
      const { data } = await api.patch(`/reservations/${id}`, { stato });
      setReservations((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Prenotazione ${stato}`);
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa prenotazione?")) return;
    try {
      await api.delete(`/reservations/${id}`);
      setReservations((prev) => prev.filter((r) => r.id !== id));
      toast.success("Prenotazione eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const inputCls =
    "bg-[#0D0C0A] border border-[rgba(201,162,101,0.2)] rounded-xl px-4 py-3 text-sm text-[#FAF7F2] placeholder-[#7A7267] focus:outline-none focus:border-[#C9A265] transition-colors duration-300";

  const saveSettings = async (patch, msg = "Impostazioni salvate") => {
    try {
      const { data } = await api.put("/settings", patch);
      setSettings(data);
      if (patch.notification_email !== undefined) setNotifyEmail(data.notification_email || "");
      toast.success(msg);
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const addClosedDate = () => {
    if (!newClosedDate) return;
    const current = settings?.blocked_dates || [];
    if (current.includes(newClosedDate)) return;
    saveSettings({ blocked_dates: [...current, newClosedDate].sort() }, "Chiusura aggiunta");
    setNewClosedDate("");
  };

  const removeClosedDate = (d) =>
    saveSettings({ blocked_dates: (settings?.blocked_dates || []).filter((x) => x !== d) }, "Chiusura rimossa");

  const filtered = filter === "tutte" ? reservations : reservations.filter((r) => (r.stato || "in attesa") === filter);
  const count = (s) => reservations.filter((r) => (r.stato || "in attesa") === s).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0D0C0A]">
      <header className="border-b border-[rgba(201,162,101,0.18)] bg-[#171512]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-cinzel text-sm tracking-[0.25em] text-[#FAF7F2]">PANNELLO PRENOTAZIONI</p>
              <p className="text-[11px] text-[#7A7267]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin/menu" data-testid="admin-menu-link" className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors">
              Menù
            </a>
            <a href="/admin/foto" data-testid="admin-photos-link" className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors">
              Foto
            </a>
            <a href="/admin/recensioni" data-testid="admin-reviews-link" className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors">
              Recensioni
            </a>
            <a href="/admin/qr" data-testid="admin-qr-link" className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors">
              QR Menù
            </a>
            <a href="/" data-testid="admin-back-to-site" className="text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors">
              Sito
            </a>
            <button
              data-testid="admin-refresh-header"
              onClick={load}
              className="flex items-center gap-2 px-5 py-2.5 border border-[rgba(201,162,101,0.4)] text-[#C9A265] text-xs uppercase tracking-[0.2em] font-cinzel rounded-full hover:bg-[#C9A265] hover:text-[#0D0C0A] transition-colors duration-300"
            >
              <RefreshCw size={14} /> Aggiorna
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Totali", value: reservations.length, testid: "admin-count-totali" },
            { label: "In attesa", value: count("in attesa"), testid: "admin-count-attesa" },
            { label: "Confermate", value: count("confermata"), testid: "admin-count-confermate" },
            { label: "Annullate", value: count("annullata"), testid: "admin-count-annullate" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-[#171512] border border-[rgba(201,162,101,0.18)] rounded-2xl px-5 py-4">
              <p className="font-serif-display text-3xl text-[#C9A265]">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#7A7267] font-cinzel">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATI.map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s.replace(" ", "-")}`}
                onClick={() => setFilter(s)}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-cinzel rounded-full border transition-all duration-300 ${
                  filter === s
                    ? "bg-[#C9A265] text-[#0D0C0A] border-[#C9A265]"
                    : "border-[rgba(201,162,101,0.3)] text-[#B0A89C] hover:border-[#C9A265] hover:text-[#C9A265]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
          </button>
        </div>

        {loading ? (
          <p data-testid="admin-loading" className="text-center text-[#7A7267] py-20 font-cinzel tracking-[0.25em] text-xs uppercase">Caricamento...</p>
        ) : filtered.length === 0 ? (
          <p data-testid="admin-empty" className="text-center text-[#7A7267] py-20 font-serif-display italic text-2xl">
            Nessuna prenotazione {filter !== "tutte" ? `"${filter}"` : "per ora"}.
          </p>
        ) : (
          <div data-testid="admin-reservations-list" className="space-y-4">
            {filtered.map((r, i) => (
              <motion.div
                key={r.id}
                data-testid={`reservation-card-${r.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="bg-[#171512] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,162,101,0.35)] rounded-2xl p-6 transition-colors duration-300"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif-display text-2xl text-[#FAF7F2]">{r.nome}</h3>
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-cinzel border rounded-full px-3 py-1 ${badgeCls[r.stato || "in attesa"]}`}>
                        {r.stato || "in attesa"}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#B0A89C]">
                      <span className="flex items-center gap-2"><CalendarDays size={14} className="text-[#C9A265]" /> {formatData(r.data)} · {r.ora}</span>
                      <span className="flex items-center gap-2"><Users size={14} className="text-[#C9A265]" /> {r.ospiti} ospiti{r.sala ? ` · ${r.sala}` : ""}</span>
                      <a href={`tel:${r.telefono}`} className="flex items-center gap-2 hover:text-[#C9A265] transition-colors"><Phone size={14} className="text-[#C9A265]" /> {r.telefono}</a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-[#C9A265] transition-colors"><Mail size={14} className="text-[#C9A265]" /> {r.email}</a>
                    </div>
                    {r.note && <p className="mt-3 text-sm text-[#7A7267] italic">"{r.note}"</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`confirm-btn-${r.id}`}
                      onClick={() => setStato(r.id, "confermata")}
                      title="Conferma"
                      className="h-10 w-10 rounded-full border border-emerald-700/50 text-emerald-300 flex items-center justify-center hover:bg-emerald-900/50 transition-colors"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      data-testid={`cancel-btn-${r.id}`}
                      onClick={() => setStato(r.id, "annullata")}
                      title="Annulla"
                      className="h-10 w-10 rounded-full border border-red-800/50 text-red-300 flex items-center justify-center hover:bg-red-950/50 transition-colors"
                    >
                      <X size={16} />
                    </button>
                    <button
                      data-testid={`delete-btn-${r.id}`}
                      onClick={() => remove(r.id)}
                      title="Elimina"
                      className="h-10 w-10 rounded-full border border-[rgba(255,255,255,0.15)] text-[#7A7267] flex items-center justify-center hover:border-red-800/50 hover:text-red-300 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div data-testid="blocked-dates-card" className="bg-[#171512] border border-[rgba(201,162,101,0.25)] rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <CalendarOff size={18} className="text-[#C9A265]" />
              <p className="font-cinzel text-xs uppercase tracking-[0.25em] text-[#C9A265]">Chiusure straordinarie</p>
            </div>
            <p className="mt-2 text-xs text-[#7A7267] leading-relaxed">
              Le date bloccate non accettano prenotazioni dal sito (ferie, festività...). Il mercoledì è già chiuso di default.
            </p>
            <div className="mt-4 flex gap-2">
              <input
                type="date"
                data-testid="blocked-date-input"
                value={newClosedDate}
                onChange={(e) => setNewClosedDate(e.target.value)}
                className={`flex-1 ${inputCls}`}
              />
              <button
                data-testid="blocked-date-add"
                onClick={addClosedDate}
                className="px-5 py-3 bg-[#C9A265] text-[#0D0C0A] text-[11px] uppercase tracking-[0.2em] font-semibold font-cinzel rounded-xl hover:bg-[#E0B87A] transition-colors duration-300"
              >
                Aggiungi
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(settings?.blocked_dates || []).map((d) => (
                <span
                  key={d}
                  data-testid={`blocked-date-${d}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(201,162,101,0.35)] text-xs text-[#FAF7F2]"
                >
                  {formatData(d)}
                  <button
                    data-testid={`blocked-date-remove-${d}`}
                    onClick={() => removeClosedDate(d)}
                    className="text-[#7A7267] hover:text-red-300 transition-colors"
                    title="Rimuovi"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {settings && (settings.blocked_dates || []).length === 0 && (
                <p className="text-xs text-[#7A7267] italic">Nessuna chiusura straordinaria programmata.</p>
              )}
            </div>
          </div>

          <div data-testid="notify-email-card" className="bg-[#171512] border border-[rgba(201,162,101,0.25)] rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <BellRing size={18} className="text-[#C9A265]" />
              <p className="font-cinzel text-xs uppercase tracking-[0.25em] text-[#C9A265]">Email notifiche prenotazioni</p>
            </div>
            <p className="mt-2 text-xs text-[#7A7267] leading-relaxed">
              Ogni nuova prenotazione dal sito arriva subito a questo indirizzo email.
            </p>
            <div className="mt-4 flex gap-2">
              <input
                type="email"
                data-testid="notify-email-input"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="ristorante@email.it"
                className={`flex-1 ${inputCls}`}
              />
              <button
                data-testid="notify-email-save"
                onClick={() => saveSettings({ notification_email: notifyEmail.trim() }, "Email notifiche salvata")}
                className="px-5 py-3 bg-[#C9A265] text-[#0D0C0A] text-[11px] uppercase tracking-[0.2em] font-semibold font-cinzel rounded-xl hover:bg-[#E0B87A] transition-colors duration-300"
              >
                Salva
              </button>
            </div>
            {settings?.notification_email && (
              <p data-testid="notify-email-active" className="mt-3 text-[11px] text-emerald-300">
                Attive su: {settings.notification_email}
              </p>
            )}
          </div>

          <div data-testid="social-links-card" className="bg-[#171512] border border-[rgba(201,162,101,0.25)] rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <Instagram size={18} className="text-[#C9A265]" />
              <p className="font-cinzel text-xs uppercase tracking-[0.25em] text-[#C9A265]">Link social</p>
            </div>
            <p className="mt-2 text-xs text-[#7A7267] leading-relaxed">
              Le icone compaiono nei contatti e in fondo al sito. Lascia vuoto per nascondere un'icona.
            </p>
            <div className="mt-4 space-y-3">
              <input
                type="url"
                data-testid="instagram-url-input"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="https://instagram.com/ristorante_milano_casale"
                className={`w-full ${inputCls}`}
              />
              <input
                type="url"
                data-testid="facebook-url-input"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                placeholder="https://facebook.com/..."
                className={`w-full ${inputCls}`}
              />
              <button
                data-testid="social-links-save"
                onClick={() => saveSettings({ instagram_url: instagramUrl.trim(), facebook_url: facebookUrl.trim() }, "Link social salvati")}
                className="px-5 py-3 bg-[#C9A265] text-[#0D0C0A] text-[11px] uppercase tracking-[0.2em] font-semibold font-cinzel rounded-xl hover:bg-[#E0B87A] transition-colors duration-300"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
